import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"

/**
 * Configuration for generating XTAB/DTAB scripts
 */
export interface ScriptConfig {
  /** Unique identifier for the table (will be sanitized) */
  tableId: string
  /**
   * When set, used verbatim as the engine table id — bypasses the
   * `tbl_<sanitised>_<timestamp>` wrapping. Used by the visual Builder so
   * the QTable name the user typed is what lands in the engine. The caller
   * is responsible for validating it's a legal identifier.
   */
  explicitOutputId?: string
  /** Row variables for the operation */
  rowVariables: string[]
  /** Column variables for the operation */
  columnVariables: string[]
  /** Selected operators/statistics */
  selectedOperators: STATISTIC_TYPE[]
  /** Operation type (XTAB or DTAB) */
  tableOperationType: TABLE_OPERATION_TYPE
  /** Optional filter to apply */
  filter?: string
  /** Whether to transpose the output */
  isTransposed?: boolean
  /** Auto-run script content to prepend */
  autoRunScriptContent?: string
  /** Elements to remove from columns */
  removedColumns?: Set<string>
  /** Elements to remove from rows */
  removedRows?: Set<string>
}

/**
 * Configuration for simple two-variable scripts (used by Chart/Table elements)
 */
export interface SimpleScriptConfig {
  /** Unique identifier for the table */
  tableId: string
  /** First variable (row variable) */
  variable1: string
  /** Second variable (column variable) */
  variable2: string
  /** Selected operators/statistics */
  selectedOperators: STATISTIC_TYPE[]
  /** Operation type (XTAB or DTAB) */
  tableOperationType: TABLE_OPERATION_TYPE
  /** Optional filter to apply */
  filter?: string
  /** Whether to transpose the output */
  isTransposed?: boolean
}

/**
 * QTABLE generation parts
 */
interface QTableParts {
  expression: string
  definitionScript: string
}

/**
 * Service for generating XTAB/DTAB scripts consistently across the application
 *
 * This service centralizes all script generation logic that was previously
 * duplicated across ChartElement, TableElement, ExcelTableContent, and other components.
 */
export class ScriptGenerationService {
  /**
   * Sanitizes a table ID to be safe for use in engine scripts
   */
  private static sanitizeTableId(tableId: string): string {
    return tableId.replace(/[^a-zA-Z0-9_]/g, "_")
  }

  /**
   * Validates operation type and provides fallback
   */
  private static validateOperationType(
    operationType: TABLE_OPERATION_TYPE | string | undefined
  ): string {
    if (!operationType) {
      console.warn("tableOperationType was undefined, using XTAB as fallback")
      return "XTAB"
    }

    if (typeof operationType === "string") {
      if (operationType === "XTAB" || operationType === "DTAB") {
        return operationType
      }
    } else if (typeof operationType === "object") {
      // Handle case where it might be the enum object
      const operationTypeStr = String(operationType)
      if (operationTypeStr === "XTAB" || operationTypeStr === "DTAB") {
        return operationTypeStr
      }
    }

    console.error(
      "Invalid operationType:",
      operationType,
      "using XTAB as fallback"
    )
    return "XTAB"
  }

  /**
   * Formats operators for script inclusion
   */
  private static formatOperators(operators: STATISTIC_TYPE[]): string {
    return operators.map((op) => `"${op}"`).join(",")
  }

  /**
   * Generates QTABLE definition parts for multiple variables
   */
  private static generateQTableParts(
    variables: string[],
    tempTableName: string
  ): QTableParts {
    if (variables.length === 0) {
      return { expression: "", definitionScript: "" }
    }

    if (variables.length === 1) {
      return { expression: `<<${variables[0]}>>`, definitionScript: "" }
    }

    // Multiple variables - need QTABLE definition
    let definitionScript = `QTABLE ${tempTableName}\n`
    variables.forEach((variable) => {
      definitionScript += `APPEND ${tempTableName} <<${variable}>>\n`
    })

    return {
      expression: tempTableName,
      definitionScript,
    }
  }

  /**
   * Generates REMOVE commands for elements to be excluded
   */
  private static generateRemoveCommands(
    removedColumns: Set<string>,
    removedRows: Set<string>,
    columnVariables: string[],
    rowVariables: string[]
  ): string {
    let removeScript = ""

    const getRemovePath = (item: string, varList: string[]): string => {
      return item.includes("//") || varList.length === 0
        ? item
        : `${varList[0]}//${item}`
    }

    // Remove column elements
    removedColumns.forEach((item) => {
      const removePath = getRemovePath(item, columnVariables)
      removeScript += `REMOVE <<${removePath}>>\n`
    })

    // Remove row elements
    removedRows.forEach((rowId) => {
      const removePath = getRemovePath(rowId, rowVariables)
      removeScript += `REMOVE <<${removePath}>>\n`
    })

    return removeScript
  }

  /**
   * Generates a complete XTAB/DTAB script with QTABLE definitions and REMOVE commands
   * Used primarily by Excel reporting components that need complex multi-variable handling
   */
  public static generateComplexScript(config: ScriptConfig): string {
    const {
      tableId,
      explicitOutputId,
      rowVariables,
      columnVariables,
      selectedOperators,
      tableOperationType,
      filter,
      autoRunScriptContent,
      removedColumns = new Set(),
      removedRows = new Set(),
    } = config

    // Generate unique table identifiers. When the caller supplies an
    // explicit id (Builder mode), use it as-is — the user picked the name
    // and the UI validated uniqueness across builders. Otherwise fall back
    // to the legacy `tbl_<sanitised>_<timestamp>` pattern.
    const uniqueTableId = explicitOutputId
      ? explicitOutputId
      : `tbl_${this.sanitizeTableId(tableId)}_${Date.now()}`
    const rowTempTable = `rowVar_${uniqueTableId}`
    const colTempTable = `colVar_${uniqueTableId}`

    // Start building script
    let script = "RESET\n"

    // Add auto-run content if provided
    if (autoRunScriptContent) {
      script += autoRunScriptContent + "\n"
    }

    // Generate QTABLE definitions for multiple variables
    const rowVarParts = this.generateQTableParts(rowVariables, rowTempTable)
    const colVarParts = this.generateQTableParts(columnVariables, colTempTable)

    // Add QTABLE definitions to script
    script += rowVarParts.definitionScript
    script += colVarParts.definitionScript

    // Validate variable expressions
    if (!rowVarParts.expression || !colVarParts.expression) {
      throw new Error(
        "Row or Column variable expression for table operation is empty."
      )
    }

    // Add REMOVE commands if needed
    const hasElementsToRemove = removedColumns.size > 0 || removedRows.size > 0
    if (hasElementsToRemove) {
      script += this.generateRemoveCommands(
        removedColumns,
        removedRows,
        columnVariables,
        rowVariables
      )
    }

    // Validate and format operation type
    const operationType = this.validateOperationType(tableOperationType)
    const operatorsString = this.formatOperators(selectedOperators)
    const filterCmd = filter ? ` UNIVERSE: <<${filter}>>` : ""

    // Add the main operation and output commands
    script += `${operationType} ${uniqueTableId} ${rowVarParts.expression} ${colVarParts.expression}${filterCmd} OPERATORS: ${operatorsString}\n`
    script += `OUTPUT ${uniqueTableId}`

    return script
  }

  /**
   * Generates a simple XTAB/DTAB script for two variables
   * Used by Chart and Table elements that work with exactly two variables
   */
  public static generateSimpleScript(config: SimpleScriptConfig): string {
    const {
      tableId,
      variable1,
      variable2,
      selectedOperators,
      tableOperationType,
      filter,
      isTransposed = false,
    } = config

    // Generate unique table identifier
    const sanitizedTableId = this.sanitizeTableId(tableId)
    const uniqueTableId = `tbl_${sanitizedTableId}_${Date.now()}`

    // Validate and format components
    const operationType = this.validateOperationType(tableOperationType)
    const operatorsString = this.formatOperators(selectedOperators)
    const filterCmd = filter ? ` UNIVERSE: <<${filter}>>` : ""

    // Build the script
    const script = `${operationType} ${uniqueTableId} <<${variable1}>> <<${variable2}>>${filterCmd} OPERATORS: ${operatorsString}\n${
      isTransposed ? `OUTPUT ${uniqueTableId} /T` : `OUTPUT ${uniqueTableId}`
    }`

    return script
  }

  /**
   * Generates a script for chart elements (wrapper around simple script)
   */
  public static generateChartScript(
    elementId: string,
    selectedVariable: string,
    breakdownVariable: string,
    selectedStatisticType: STATISTIC_TYPE,
    tableOperationType: TABLE_OPERATION_TYPE,
    filter?: string
  ): string {
    return this.generateSimpleScript({
      tableId: elementId,
      variable1: selectedVariable,
      variable2: breakdownVariable,
      selectedOperators: [selectedStatisticType],
      tableOperationType,
      filter,
    })
  }

  /**
   * Generates a script for table elements (wrapper around simple script)
   */
  public static generateTableScript(
    elementId: string,
    crossTabVariables: [string, string],
    selectedOperators: STATISTIC_TYPE[],
    tableOperationType: TABLE_OPERATION_TYPE,
    filter?: string,
    isTransposed?: boolean
  ): string {
    return this.generateSimpleScript({
      tableId: elementId,
      variable1: crossTabVariables[0],
      variable2: crossTabVariables[1],
      selectedOperators,
      tableOperationType,
      filter,
      isTransposed,
    })
  }

  /**
   * Generates a script for pie chart elements with empty QTABLE handling
   * This creates an empty QTABLE and uses it as the breakdown variable
   */
  public static generatePieChartWithEmptyQTableScript(
    elementId: string,
    selectedVariable: string,
    selectedStatisticType: STATISTIC_TYPE,
    filter?: string
  ): string {
    const sanitizedElementId = this.sanitizeTableId(elementId)
    const uniqueTableId = `tbl_${sanitizedElementId}_${Date.now()}`
    const tempTableId = `Temp_${sanitizedElementId}_${Date.now()}`

    let script = `QTABLE ${tempTableId}\n`

    const filterCmd = filter ? ` UNIVERSE: <<${filter}>>` : ""
    const baseCmd = `XTAB ${uniqueTableId} <<${selectedVariable}>> ${tempTableId}`

    script += `${baseCmd}${filterCmd} OPERATORS: "${selectedStatisticType}"\n`
    script += `OUTPUT ${uniqueTableId}\n`
    script += `REMOVE ${tempTableId}`

    return script
  }

  /**
   * Generates a script for pie chart elements with special QTABLE handling
   */
  public static generatePieChartScript(
    elementId: string,
    rowVariable: string,
    breakdownVariables: string[],
    selectedStatisticType: STATISTIC_TYPE,
    filter?: string
  ): string {
    const sanitizedElementId = this.sanitizeTableId(elementId)
    const uniqueTableId = `tbl_${sanitizedElementId}_${Date.now()}`
    const tempTableId = `temp_${uniqueTableId}`

    let script = `QTABLE ${tempTableId}\n`

    // Add breakdown variables to temp table
    breakdownVariables.forEach((variable) => {
      script += `APPEND ${tempTableId} <<${variable}>>\n`
    })

    const filterCmd = filter ? ` UNIVERSE: <<${filter}>>` : ""
    const baseCmd = `XTAB ${uniqueTableId} <<${rowVariable}>> ${tempTableId}`

    script += `${baseCmd}${filterCmd} OPERATORS: "${selectedStatisticType}"\n`
    script += `OUTPUT ${uniqueTableId}`

    return script
  }

  /**
   * Generates a script for time series elements
   */
  public static generateTimeSeriesScript(
    elementId: string,
    selectedVariable: string,
    timeVariable: string,
    selectedStatisticType: STATISTIC_TYPE,
    tableOperationType: TABLE_OPERATION_TYPE,
    filter?: string
  ): string {
    return this.generateSimpleScript({
      tableId: elementId,
      variable1: selectedVariable,
      variable2: timeVariable,
      selectedOperators: [selectedStatisticType],
      tableOperationType,
      filter,
    })
  }

  /**
   * Helper method to create a ScriptConfig from common element properties
   */
  public static createScriptConfig(
    elementId: string,
    rowVariables: string[],
    columnVariables: string[],
    selectedOperators: STATISTIC_TYPE[],
    tableOperationType: TABLE_OPERATION_TYPE,
    options: {
      filter?: string
      autoRunScriptContent?: string
      removedColumns?: string[]
      removedRows?: string[]
    } = {}
  ): ScriptConfig {
    return {
      tableId: elementId,
      rowVariables,
      columnVariables,
      selectedOperators,
      tableOperationType,
      filter: options.filter,
      autoRunScriptContent: options.autoRunScriptContent,
      removedColumns: new Set(options.removedColumns || []),
      removedRows: new Set(options.removedRows || []),
    }
  }
}

export default ScriptGenerationService
